import * as React from 'react';
import { memoizeFunction, mergeStyleSets, Stack } from '@fluentui/react';
import { ActionButton } from 'common/components/Buttons/v8';
import { PageWrapper } from 'common/components/Layout/PageWrapper';
import { usePageLoadTracker } from 'common/utilities/PageLoadTracker';
import { RegionAgnosticMarketingPage } from 'common/utilities/URLConstants';
import { AddDeviceContent, AddDeviceFooter, downloadDefenderTelemetryPrefix } from 'features/device/AddDevice/AddDeviceDialog';
import { useLocalizedStrings } from 'i18n';

export const getClassnames = memoizeFunction(() =>
  mergeStyleSets({
    content: {
      // matches max-width of AddDeviceDialog
      maxWidth: 740,
    },
  })
);

/**
 * The 'Get Defender' page, which is an UNAUTHENTICATED page,
 * is a CTA from the Monthly Digest email & renders the AddDeviceDialog.
 *
 * @returns {React.ReactElement} the Download Defender page
 */
export const GetDefenderPage: React.FC = () => {
  // i18next-extract-mark-ns-start devices
  const { t } = useLocalizedStrings('devices');
  const query = new URLSearchParams(window.location.search);
  const campaign = query.get('campaign');
  const source = query.get('source');
  const sourceEmail = query.get('sourceEmail');
  const sourceEmailCsid = query.get('sourceEmailCsid');
  const version = query.get('v');

  const classNames = getClassnames();

  const telemetryPrefix = downloadDefenderTelemetryPrefix;
  const telemetryProps = {
    Source: source,
    Campaign: campaign,
    SourceEmail: sourceEmail,
    SourceEmailCsidd: sourceEmailCsid,
    V: version,
    Version: version,
  };

  usePageLoadTracker(`${telemetryPrefix}PageView`, [], telemetryProps);

  const title = t('AddDevice.Dialog.Title');

  const secondaryButton = (
    <Stack
      // to properly align the rendered link:
      verticalAlign="center"
      verticalFill
    >
      <ActionButton
        href={RegionAgnosticMarketingPage}
        text={t('AddDevice.Dialog.Dismiss')}
        target="_blank"
        telemetryEventName={`${telemetryPrefix}CloseClick`}
        telemetryProps={telemetryProps}
      />
    </Stack>
  );

  return (
    <PageWrapper
      pageName={telemetryPrefix}
      title={title}
      description={t('Page.MetaContent')}
      backButton={{ show: false }}
      showHeading
      panes={{
        singlePane: (
          <Stack tokens={{ childrenGap: 32, padding: '32px 0' }} horizontalAlign="center">
            <Stack.Item className={classNames.content} grow>
              <AddDeviceContent headingId={`${telemetryPrefix}Title`} title={title} telemetryProps={telemetryProps} />
            </Stack.Item>
            <AddDeviceFooter onRenderSecondaryButton={() => secondaryButton} telemetryProps={telemetryProps} />
          </Stack>
        ),
      }}
      showScrollUpButton={false}
      useStickyScrolling={false}
      heading={undefined}
      paddingOverride="0" // padding already comes from AddDeviceContent
    />
  );
  // i18next-extract-mark-ns-end devices
};

export default GetDefenderPage;
